import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { Heart, Mail, Lock, User, Stethoscope, MessageCircle, Loader2 } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('care_seeker');
  const [loading, setLoading] = useState(false);

  // New: tab selector — 'password' | 'whatsapp' (seekers only)
  const [authMode, setAuthMode] = useState('password');

  // WhatsApp OTP state
  const [otpMobile, setOtpMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpStage, setOtpStage] = useState('request'); // request | verify
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpName, setOtpName] = useState('');

  // Consent
  const [consent, setConsent] = useState(false);
  const [policy, setPolicy] = useState({
    terms_version: 'v1.0',
    privacy_version: 'v1.0',
    terms_url: '#',
    privacy_url: '#',
  });

  const googleBtnRef = useRef(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'provider') setRole('care_provider');
    axios.get(`${API}/policies/versions`).then((r) => setPolicy(r.data)).catch(() => {});
  }, [searchParams]);

  // Google Identity Services setup (robust: load script if needed, then poll
  // until both the GIS library AND our button container are ready before
  // rendering — handles slow script loads and SPA navigation re-mounts).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || role !== 'care_seeker') return;
    let cancelled = false;

    const ensureScript = () => {
      if (window.google?.accounts?.id) return;
      if (document.getElementById('gis-client-script')) return;
      const s = document.createElement('script');
      s.id = 'gis-client-script';
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    };

    const tryRender = (attempt = 0) => {
      if (cancelled) return;
      if (window.google?.accounts?.id && googleBtnRef.current) {
        renderGoogleButton();
        return;
      }
      if (attempt < 40) {
        // retry for ~10s (40 * 250ms) while the script / DOM settle
        setTimeout(() => tryRender(attempt + 1), 250);
      } else {
        console.warn('[auth] Google Sign-In could not initialize (GIS script blocked or slow).');
      }
    };

    ensureScript();
    tryRender();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isLogin, authMode]);

  const renderGoogleButton = () => {
    if (!window.google?.accounts?.id || !googleBtnRef.current) return;
    try {
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline', size: 'large', width: 320, text: 'continue_with',
      });
    } catch (e) {
      console.warn('[auth] Google renderButton failed:', e);
    }
  };

  const handleGoogleResponse = async (resp) => {
    if (!isLogin && !consent) {
      toast.error('Please accept Terms & Privacy to continue');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/google`, {
        id_token: resp.credential,
        role: 'care_seeker',
        accept_terms_version: !isLogin ? policy.terms_version : null,
        accept_privacy_version: !isLogin ? policy.privacy_version : null,
      });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}!`);
      const convSid = await maybeConvertGuest(r.data.token);
      navigate(convSid ? `/chat/${convSid}` : '/seeker-dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  // Phase 4: after auth, link any pending guest chat to this account and resume it.
  const maybeConvertGuest = async (userToken) => {
    const guestToken = localStorage.getItem('pending_guest_token');
    const sessionId = localStorage.getItem('pending_guest_session');
    if (!guestToken || !sessionId) return null;
    try {
      await axios.post(`${API}/guest/convert`, { session_id: sessionId, guest_token: guestToken }, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      return sessionId;
    } catch (e) {
      return null;
    } finally {
      localStorage.removeItem('pending_guest_token');
      localStorage.removeItem('pending_guest_session');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !consent) {
      toast.error('Please accept Terms & Privacy to continue');
      return;
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, role };

      const response = await axios.post(endpoint, payload);
      login(response.data.token, response.data.user);

      // Record consent server-side for signups
      if (!isLogin && consent) {
        const headers = { Authorization: `Bearer ${response.data.token}` };
        await Promise.all([
          axios.post(`${API}/consents`, { consent_type: 'terms', version: policy.terms_version, accepted: true, context: 'signup' }, { headers }).catch(() => {}),
          axios.post(`${API}/consents`, { consent_type: 'privacy', version: policy.privacy_version, accepted: true, context: 'signup' }, { headers }).catch(() => {}),
        ]);
      }

      const convSid = await maybeConvertGuest(response.data.token);
      if (convSid) {
        navigate(`/chat/${convSid}`);
        return;
      }

      const dashboardPath = response.data.user.role === 'care_seeker'
        ? '/seeker-dashboard'
        : response.data.user.role === 'admin'
        ? '/admin'
        : '/provider-dashboard';
      navigate(dashboardPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const isProvider = role === 'care_provider';

  // ============== WhatsApp OTP handlers ==============
  const requestOtp = async () => {
    if (!otpMobile.trim()) {
      toast.error('Enter your mobile number');
      return;
    }
    if (!isLogin && !consent) {
      toast.error('Please accept Terms & Privacy to continue');
      return;
    }
    setOtpRequesting(true);
    try {
      await axios.post(`${API}/auth/otp/request`, { mobile: otpMobile, purpose: isLogin ? 'login' : 'signup' });
      toast.success('OTP sent to WhatsApp');
      setOtpStage('verify');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send OTP');
    } finally {
      setOtpRequesting(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim() || otpCode.length < 4) {
      toast.error('Enter the OTP from WhatsApp');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/otp/verify`, {
        mobile: otpMobile,
        code: otpCode,
        name: otpName || undefined,
        accept_terms_version: !isLogin ? policy.terms_version : undefined,
        accept_privacy_version: !isLogin ? policy.privacy_version : undefined,
      });
      login(r.data.token, r.data.user);
      toast.success(r.data.is_new_user ? 'Welcome!' : 'Welcome back');
      navigate('/seeker-dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const ConsentRow = () => (
    <label className="flex items-start gap-2 mt-3 text-xs text-gray-600" data-testid="auth-consent-row">
      <input
        type="checkbox"
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        className="mt-0.5 accent-[#7D202B]"
        data-testid="auth-consent-checkbox"
      />
      <span>
        I agree to the{' '}
        <a href={policy.terms_url} target="_blank" rel="noreferrer" className="underline">Terms of Service</a>
        {' '}and{' '}
        <a href={policy.privacy_url} target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.
      </span>
    </label>
  );

  return (
    <div className={`min-h-screen flex flex-col ${isProvider ? 'bg-blue-50' : 'bg-[#FDF2F0]'}`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-center">
        <Heart className={isProvider ? 'text-blue-600' : 'text-[#7D202B]'} size={28} />
        <span className="ml-2 text-xl font-semibold text-[#7D202B]">Veyonika</span>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Role Toggle */}
          <div className="bg-white rounded-2xl p-1 mb-6 flex shadow-sm">
            <button
              type="button"
              data-testid="role-care-seeker-btn"
              onClick={() => setRole('care_seeker')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                !isProvider
                  ? 'bg-[#7D202B] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <User size={18} />
              Care Seeker
            </button>
            <button
              type="button"
              data-testid="role-care-provider-btn"
              onClick={() => setRole('care_provider')}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                isProvider
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Stethoscope size={18} />
              Care Giver
            </button>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-1 text-center">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-gray-500 text-sm mb-4 text-center">
              {isLogin
                ? `Sign in as ${isProvider ? 'Care Giver' : 'Care Seeker'}`
                : `Register as ${isProvider ? 'Care Giver' : 'Care Seeker'}`
              }
            </p>

            {/* Seeker-only: Password / WhatsApp mode toggle */}
            {!isProvider && (
              <div className="bg-gray-100 rounded-xl p-1 mb-5 flex text-sm" data-testid="auth-mode-toggle">
                <button
                  type="button"
                  onClick={() => setAuthMode('password')}
                  className={`flex-1 py-2 rounded-lg ${authMode === 'password' ? 'bg-white shadow font-medium' : 'text-gray-500'}`}
                  data-testid="auth-mode-password"
                >
                  Email / Password
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMode('whatsapp')}
                  className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-1 ${authMode === 'whatsapp' ? 'bg-white shadow font-medium' : 'text-gray-500'}`}
                  data-testid="auth-mode-whatsapp"
                >
                  <MessageCircle size={14} /> WhatsApp OTP
                </button>
              </div>
            )}

            {/* ===== WhatsApp OTP form (seeker only) ===== */}
            {!isProvider && authMode === 'whatsapp' ? (
              <div className="space-y-4" data-testid="auth-otp-form">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                    <input
                      type="text"
                      value={otpName}
                      onChange={(e) => setOtpName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base"
                      placeholder="Your full name"
                      data-testid="otp-name-input"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp mobile</label>
                  <input
                    type="tel"
                    value={otpMobile}
                    onChange={(e) => setOtpMobile(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base"
                    placeholder="+91 ..."
                    disabled={otpStage === 'verify'}
                    data-testid="otp-mobile-input"
                  />
                </div>
                {otpStage === 'verify' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">6-digit code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base tracking-widest"
                      placeholder="••••••"
                      data-testid="otp-code-input"
                    />
                  </div>
                )}
                {!isLogin && <ConsentRow />}
                {otpStage === 'request' ? (
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={otpRequesting}
                    className="w-full py-3.5 rounded-xl font-medium text-white bg-[#7D202B] hover:bg-[#963A42] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="otp-request-btn"
                  >
                    {otpRequesting && <Loader2 className="animate-spin" size={16} />}
                    Send OTP on WhatsApp
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={verifyOtp}
                      disabled={loading}
                      className="w-full py-3.5 rounded-xl font-medium text-white bg-[#7D202B] hover:bg-[#963A42] disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="otp-verify-btn"
                    >
                      {loading && <Loader2 className="animate-spin" size={16} />}
                      Verify &amp; {isLogin ? 'Sign in' : 'Create account'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setOtpStage('request'); setOtpCode(''); }}
                      className="w-full text-xs text-gray-500 underline"
                      data-testid="otp-back-btn"
                    >
                      Use a different number
                    </button>
                  </>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        data-testid="name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#963A42] focus:border-transparent text-base"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#963A42] focus:border-transparent text-base"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-xs text-[#7D202B] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      name="password"
                      data-testid="password-input"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#963A42] focus:border-transparent text-base"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {!isLogin && <ConsentRow />}

                <button
                  type="submit"
                  data-testid="auth-submit-btn"
                  disabled={loading}
                  className={`w-full py-3.5 rounded-xl font-medium text-white transition-all disabled:opacity-50 ${
                    isProvider
                      ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700'
                      : 'bg-[#7D202B] hover:bg-[#963A42] active:bg-[#7D202B]'
                  }`}
                >
                  {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                </button>
              </form>
            )}

            {/* Google SSO (seeker only, when client id is set) */}
            {!isProvider && GOOGLE_CLIENT_ID && (
              <>
                <div className="flex items-center gap-3 my-4 text-xs text-gray-400">
                  <div className="flex-1 h-px bg-gray-200" />
                  or
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div ref={googleBtnRef} className="flex justify-center" data-testid="google-signin-btn" />
              </>
            )}

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode-btn"
                onClick={() => setIsLogin(!isLogin)}
                className={`text-sm ${isProvider ? 'text-blue-600' : 'text-[#7D202B]'} font-medium`}
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
